import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { findStep, type AuthoringDoc } from './doc.js';
import { editableNodeIds, findNode } from './tree.js';

enablePatches();

// SessionCtx — Plasmic's `studioCtx.change()` discipline in ~30 lines, without MobX
// (mobile-native-v1.md, The editor). EVERY mutation funnels through `applyChange`, which runs it
// under immer and keeps the INVERSE patch. That is the entire undo system: no command objects, no
// action log, no snapshots of the doc.
//
// Vanilla zustand on purpose — this package is headless. `apps/web` binds it with `useStore`.

/** Capped so a long session cannot grow the stack without bound. 200 is generous: it is ~200
 *  inspector edits, and nobody undoes further than that. */
const UNDO_LIMIT = 200;

interface PatchPair {
  patches: Patch[];
  inverse: Patch[];
}

export interface SessionState {
  doc: AuthoringDoc;
  /** Ordered block ids in the focused step. NOT undoable — history restores the document only. */
  selection: string[];
  /** Most recently targeted selection member, independent of structural/document ordering. */
  selectionAnchor: string | null;
  focusedStepId: string;
  undoStack: PatchPair[];
  redoStack: PatchPair[];
  /** What the top undo entry is coalescing. Internal; see `applyChange`. */
  mergeKey: string | null;

  /** `mergeKey` COALESCES consecutive changes into one undo entry. Without it, typing a headline
   *  into the inspector pushes one entry per keystroke and ⌘Z undoes a single letter. Callers pass
   *  `${nodeId}.${field}`, so moving to another field — or another node — breaks the chain by
   *  itself, and nothing has to remember to "commit" anything. */
  applyChange: (mutate: (draft: AuthoringDoc) => void, mergeKey?: string) => void;
  undo: () => void;
  redo: () => void;
  select: (id: string | null, mode?: 'replace' | 'toggle' | 'range') => void;
  selectMany: (ids: readonly string[], anchor?: string | null) => void;
  focusStep: (id: string) => void;
}

export type SessionStore = StoreApi<SessionState>;

export function createSession(doc: AuthoringDoc): SessionStore {
  const firstStep = doc.steps[0];
  if (!firstStep) throw new Error('createSession: a doc must have at least one step');

  return createStore<SessionState>((set, get) => ({
    doc,
    selection: [],
    selectionAnchor: null,
    focusedStepId: firstStep.id,
    undoStack: [],
    redoStack: [],
    mergeKey: null,

    applyChange: (mutate, mergeKey) => {
      const state = get();
      // Never hand a caller's function directly to Immer. TypeScript permits a value-returning
      // callback where `void` is expected, and mutating helpers such as `moveNodes` intentionally
      // return useful IDs. If a concise arrow leaks that value to Immer it sees BOTH a modified
      // draft and a replacement value and throws. The wrapper makes `applyChange` mutation-only at
      // runtime as well as in its public type.
      const [next, patches, inverse] = produceWithPatches(state.doc, (draft) => {
        mutate(draft);
      });

      // A change that changed nothing must not push an undo entry — otherwise ⌘Z becomes a game of
      // "how many no-ops did the inspector emit while I was clicking around".
      if (patches.length === 0) return;

      const top = state.undoStack[state.undoStack.length - 1];
      const coalesce = mergeKey !== undefined && mergeKey === state.mergeKey && top !== undefined;

      // The INVERSE concatenates in REVERSE (`inverse` then `top.inverse`) — undoing a coalesced
      // run must replay the individual undos newest-first, or an insert-then-edit merges into a
      // pair that restores the edit onto a node the first inverse already removed.
      const entry: PatchPair = coalesce
        ? { patches: [...top.patches, ...patches], inverse: [...inverse, ...top.inverse] }
        : { patches, inverse };
      const stack = coalesce ? state.undoStack.slice(0, -1) : state.undoStack;

      set({
        doc: next,
        undoStack: [...stack, entry].slice(-UNDO_LIMIT),
        redoStack: [],
        mergeKey: mergeKey ?? null,
        // Deleting the selected node — or the focused step — through applyChange leaves both
        // pointing at something that no longer exists. `stepToExperienceDocument` THROWS on a
        // missing step, so this is the canvas crashing, not a cosmetic glitch.
        ...reconcile(next, state),
      });
    },

    undo: () => {
      const { doc, undoStack, redoStack } = get();
      const entry = undoStack[undoStack.length - 1];
      if (!entry) return;
      const next = applyPatches(doc, entry.inverse);
      set({
        doc: next,
        undoStack: undoStack.slice(0, -1),
        redoStack: [...redoStack, entry],
        mergeKey: null,
        ...reconcile(next, get()),
      });
    },

    redo: () => {
      const { doc, undoStack, redoStack } = get();
      const entry = redoStack[redoStack.length - 1];
      if (!entry) return;
      const next = applyPatches(doc, entry.patches);
      set({
        doc: next,
        undoStack: [...undoStack, entry].slice(-UNDO_LIMIT),
        redoStack: redoStack.slice(0, -1),
        mergeKey: null,
        ...reconcile(next, get()),
      });
    },

    // Both break a coalescing run: an edit to the same field of the same node, either side of a
    // selection change, is two separate things the author did.
    select: (id, mode = 'replace') => {
      const state = get();
      if (id === null) {
        set({ selection: [], selectionAnchor: null, mergeKey: null });
        return;
      }

      // Doc-wide lookup: the focused step may live inside a variant (ADR 0015's V2 slice).
      const step = findStep(state.doc, state.focusedStepId);
      if (!step || !findNode(step.root, id)) return;

      let selection: string[];
      if (mode === 'toggle') {
        selection = state.selection.includes(id)
          ? state.selection.filter((candidate) => candidate !== id)
          : [...state.selection, id];
      } else if (mode === 'range' && state.selectionAnchor) {
        const order = editableNodeIds(step.root);
        const from = order.indexOf(state.selectionAnchor);
        const to = order.indexOf(id);
        selection = from < 0 || to < 0
          ? [id]
          : order.slice(Math.min(from, to), Math.max(from, to) + 1);
      } else {
        selection = [id];
      }

      // The step root can be inspected alone, but cannot participate in a structural batch.
      if (selection.length > 1) selection = selection.filter((candidate) => candidate !== step.root.id);
      set({
        selection,
        selectionAnchor: selection.includes(id) ? id : selection[selection.length - 1] ?? null,
        mergeKey: null,
      });
    },
    selectMany: (ids, anchor) => {
      const state = get();
      const step = findStep(state.doc, state.focusedStepId);
      if (!step) return;
      const unique = [...new Set(ids)].filter((id) => Boolean(findNode(step.root, id)));
      const selection = unique.length > 1 ? unique.filter((id) => id !== step.root.id) : unique;
      const preferred = anchor && selection.includes(anchor) ? anchor : selection[selection.length - 1] ?? null;
      set({ selection, selectionAnchor: preferred, mergeKey: null });
    },
    focusStep: (id) => set({ focusedStepId: id, selection: [], selectionAnchor: null, mergeKey: null }),
  }));
}

/** After an undo/redo the selected node or the focused step may no longer exist — undoing an
 *  "insert" deletes the very node the insert selected. A dangling selection is a crash in the
 *  inspector, so it is pruned here rather than defended against in every consumer. */
function reconcile(
  doc: AuthoringDoc,
  state: Pick<SessionState, 'selection' | 'selectionAnchor' | 'focusedStepId'>
): Pick<SessionState, 'selection' | 'selectionAnchor' | 'focusedStepId'> {
  // Doc-wide: keeps focus inside a variant across edits/undo. The `doc.steps[0]` fallback is the
  // right landing when the focused step's whole VARIANT was deleted (or an undo removed it).
  const step = findStep(doc, state.focusedStepId) ?? (doc.steps[0] as AuthoringDoc['steps'][number]);
  const selection = state.selection.filter((id) => findNode(step.root, id) !== undefined);

  return {
    focusedStepId: step.id,
    selection,
    selectionAnchor:
      state.selectionAnchor && selection.includes(state.selectionAnchor)
        ? state.selectionAnchor
        : selection[selection.length - 1] ?? null,
  };
}
